import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { resolveActiveAreaId } from '../navigation/nav-area.model';

/**
 * EB-4038 — remembers the last URL the user was on within each product area
 * (CMT / Brand Manager / Call Viewer), so the area rail can restore that exact
 * spot when the user switches back instead of dropping them on the area root.
 *
 * The full `urlAfterRedirects` is stored (query params + fragment included), so
 * a CMT deep link (`/13279/ua/19839/account-details`) and a Call Viewer filter
 * URL (`/calls?customerId=…&singleDate=…`) both round-trip. In-memory only —
 * the map lives for the session and is dropped on hard reload / logout.
 */
@Injectable({ providedIn: 'root' })
export class AreaStateService {
  private readonly router = inject(Router);
  private readonly lastUrlByArea = new Map<string, string>();

  constructor() {
    // Capture the URL already active at construction — this service is created lazily
    // (when the rail first injects it), so the app's initial NavigationEnd may have
    // fired before we subscribed. Without this, a hard-loaded deep link would not be
    // remembered for its area until the next navigation.
    this.record(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.record(e.urlAfterRedirects));
  }

  private record(url: string): void {
    this.lastUrlByArea.set(resolveActiveAreaId(url), url);
  }

  /** Last URL visited in the given area, or undefined if never visited this session. */
  lastUrl(areaId: string): string | undefined {
    return this.lastUrlByArea.get(areaId);
  }
}
